import { useEffect, useState } from "react";
import { Download, Share, X } from "lucide-react";
import { useT } from "../lib/i18n";
import { Logo } from "./Chrome";

const KEY = "wis_install_dismissed";
const isIOS = () => /iphone|ipad|ipod/i.test(navigator.userAgent) && !window.MSStream;
const isStandalone = () => window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone;

// Shows a polite install suggestion only after real engagement (several page views or 40 seconds).
export function InstallPrompt({ path }) {
  const { t } = useT();
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [engaged, setEngaged] = useState(false);

  useEffect(() => {
    const h = (e) => { e.preventDefault(); setDeferred(e); };
    window.addEventListener("beforeinstallprompt", h);
    const timer = setTimeout(() => setEngaged(true), 40000);
    return () => { window.removeEventListener("beforeinstallprompt", h); clearTimeout(timer); };
  }, []);

  useEffect(() => {
    const n = Number(sessionStorage.getItem("wis_views") || 0) + 1;
    sessionStorage.setItem("wis_views", String(n));
    if (n >= 4) setEngaged(true);
  }, [path]);

  useEffect(() => {
    const dismissed = Number(localStorage.getItem(KEY) || 0);
    if (!engaged || isStandalone() || Date.now() - dismissed < 14 * 864e5) return;
    if (deferred || isIOS()) setShow(true);
  }, [engaged, deferred]);

  if (!show) return null;
  const close = () => { localStorage.setItem(KEY, String(Date.now())); setShow(false); };
  const install = async () => {
    deferred.prompt();
    await deferred.userChoice.catch(() => {});
    close();
  };

  return (
    <div className="fixed inset-x-3 bottom-24 z-50 mx-auto max-w-md rounded-2xl border border-slate-200 bg-white p-4 shadow-xl fade-up md:bottom-6" data-testid="install-prompt" role="dialog">
      <button onClick={close} className="absolute right-2 top-2 grid h-10 w-10 place-items-center text-slate-500" aria-label={t("later")} data-testid="install-close-btn"><X size={20} /></button>
      <div className="flex items-center gap-3 pr-8">
        <Logo size={52} testid="install-logo" />
        <div>
          <p className="font-bold text-[color:var(--navy)]">{t("install_t")}</p>
          <p className="text-sm text-slate-600">{t("install_sub")}</p>
        </div>
      </div>
      {deferred ? (
        <div className="mt-4 flex gap-2">
          <button className="btn-ghost !min-h-[48px] !text-base flex-1" onClick={close} data-testid="install-later-btn">{t("later")}</button>
          <button className="btn-orange !min-h-[48px] !text-base flex-1" onClick={install} data-testid="install-accept-btn"><Download size={18} />{t("install_btn")}</button>
        </div>
      ) : (
        <p className="mt-3 flex items-start gap-2 rounded-xl bg-slate-50 p-3 text-sm text-slate-700" data-testid="install-ios-guide"><Share size={18} className="mt-0.5 shrink-0" />{t("ios_install")}</p>
      )}
    </div>
  );
}
